import React from 'react';
import {
  ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { PlanMetrics } from '../../utils/capacityPlan';
import { ChartColors } from '../../hooks';
import { StatusDonut } from './StatusDonut';
import './PlanCharts.css';

interface PlanChartsProps {
  metrics: PlanMetrics;
  labels: string[];
  colors: ChartColors;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString();

export const PlanCharts: React.FC<PlanChartsProps> = ({ metrics, labels, colors }) => {
  // Series color per discipline, from the validated chart palette.
  const disciplineColor = (i: number) => colors.series[i % colors.series.length];

  // Cumulative + weekly man-hours over time.
  const trendData = labels.map((label, i) => ({
    label,
    weekly: Math.round(metrics.weeklyManHours[i]),
    cumulative: Math.round(metrics.cumulativeManHours[i]),
  }));

  // Stacked man-hours by discipline per week.
  const stackData = labels.map((label, i) => {
    const row: Record<string, number | string> = { label };
    metrics.byDiscipline.forEach(d => { row[d.label] = Math.round(d.perWeek[i]); });
    return row;
  });

  // Donut: total man-hours split by discipline.
  const donutSegments = metrics.byDiscipline
    .map((d, i) => ({ key: d.key, label: d.label, value: Math.round(d.total), color: disciplineColor(i) }))
    .filter(s => s.value > 0);

  const axisTick = { fill: colors.axis, fontSize: 11 };

  return (
    <>
      <div className="plan-charts-row">
        <div className="plan-chart-card">
          <h3 className="plan-chart-title">Man-Hours Over Time</h3>
          <div className="plan-chart-body">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={trendData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <defs>
                  <linearGradient id="planCum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.series[0]} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={colors.series[0]} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: colors.grid }} tickLine={false} minTickGap={10} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={44} tickFormatter={fmtInt} />
                <Tooltip formatter={(v: unknown) => `${fmtInt(Number(v))} h`} contentStyle={{ background: 'var(--surface-3)', border: '1px solid var(--border-3)', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="cumulative" name="Cumulative" stroke={colors.series[0]} strokeWidth={2.5} fill="url(#planCum)" dot={false} />
                <Line type="monotone" dataKey="weekly" name="Weekly" stroke={colors.series[1]} strokeWidth={2} strokeDasharray="4 3" dot={false} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="plan-chart-card">
          <h3 className="plan-chart-title">Hours by Discipline</h3>
          <div className="plan-chart-body plan-donut-body">
            <StatusDonut total={Math.round(metrics.totalManHours)} segments={donutSegments} centerLabel="Hours" />
          </div>
        </div>
      </div>

      <div className="plan-chart-card">
        <h3 className="plan-chart-title">Weekly Load by Discipline</h3>
        <div className="plan-chart-body">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stackData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: colors.grid }} tickLine={false} minTickGap={10} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={44} tickFormatter={fmtInt} />
              <Tooltip formatter={(v: unknown) => `${fmtInt(Number(v))} h`} contentStyle={{ background: 'var(--surface-3)', border: '1px solid var(--border-3)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {metrics.byDiscipline.map((d, i) => (
                <Bar key={d.key} dataKey={d.label} stackId="load" fill={disciplineColor(i)} radius={i === metrics.byDiscipline.length - 1 ? [3, 3, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};
